import { FeatureFlagIds } from "../../../../utils/featureFlagIds";
import React, {JSX, useEffect, useState} from 'react';
import Grid, {GridProps} from '@mui/material/Grid';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import EditIcon from '@mui/icons-material/Edit';
import IconButton from '@mui/material/IconButton';
import MapIcon from '@mui/icons-material/Map';
import {useDispatch, useSelector} from 'react-redux';
import {AppState} from '../../../../store/rootReducer';
import {applicationIsPastDesking, canUnderwrite, getApplicant, isAnyRequiredChecklistIncomplete} from '../../../../utils/appUtils';
import ApplicationDetailsCard from '../../../ApplicationDetailsCard';
import {IConfiguration} from '../../../../Data/ConfigurationData';
import {getApplicationPerformanceEvaluation, updateEditingApp} from '../../../../store/application/actions';
import {TabName} from '../OrderEditorTabs';
import VehicleEditorDialog from '../../../Dialogs/VehicleEditorDialog/VehicleEditorDialog';
import {updateAmsVehicleAsync} from '../../../../Data/VehicleData';
import {OverviewATC} from '../OverviewATC';
import {Constants, permissions} from '../../../../utils/constants';
import {hasExistingCustomerInformation, userHasPermission} from '../../../../utils/utils';
import {useOutstandingApplicationCustomerStipulations} from '../../../../Data/StipulationData';
import {VehicleInfoCard} from "../VehicleCard/VehicleInfoCard";
import {StatusWorkflowComponent} from '../StatusWorkflowComponent';
import ChatIcon from '@mui/icons-material/Chat';
import {ApplicationChatComponent} from '../../../ApplicationChatComponent';
import {InvalidStipulationsCard} from '../../../InvalidStipulationsCard/InvalidStipulationsCard';
import {NotesCard} from '../../../NotesCard';
import {ExistingCustomerInformation} from '../ExistingCustomerInformation';
import {
  ApplicantPerformanceIndicatorRankViewer,
  ApplicationPerformanceIndicatorEvaluationResult,
  ApplicationQuoteType, usesFinanceQuote,
  hasValue,
  LoadingPanel,
  PermissionSet, Vehicle
} from '@amsanalytics/react-shared-library';
import {AmsApplicationInfoCard} from "../../../AmsApplicationInfoCard/AmsApplicationInfoCard";
import {useBusinessRulesEngine} from "../../../../Data/BusinessRulesData";
import {DecisionBoxIndirectDealerView} from "../DecisionBoxIndirectDealerView";
import DecisionBoxIndirectUnderwriterView from "../../../DecisionBoxIndirectUnderwriterView";
import {IndirectApplicationHistoryCard} from "../IndirectApplicationHistoryCard";
import {CreditQualityAndRiskMetricsCard} from "../CreditQualityAndRiskMetricsCard";
import {IndirectApplicationDecisionInfoCard} from "../IndirectApplicationDecisionInfoCard";
import {CustomerCreditBreakdownCard} from "../CustomerCreditBreakdownCard";
import {IApplication} from "../../../../Interfaces/Application/IApplication";
import produce from "immer";
import {clearSlotsHiddenByQuoteTypeChange} from "../../../../utils/assignableRoleUtils";
import {IEmployee} from "../../../../Interfaces/Admin/IEmployee";
import {isApplicationBeforeOldQuoterCutoff, isIndirectConvertedToFinance} from "../../../../utils/quoteUtils";
import {PreviousAmsLoansCard} from "../PreviousAmsLoansCard";
import {isNil} from 'ramda';
import {SET_INCOMPLETE_APPCUSTOMERS_STIPULATION_CATEGORIES} from '../../../../store/application/types';
import {ApplicationSummaryCard} from "../../../ApplicationSummaryCard/ApplicationSummaryCard";
import styles from './_overview.module.scss';
import localforage from "localforage";
import FinanceQuoterContainer from "../../../Quoters/FinanceQuoter/FinanceQuoterContainer";
import LeaseQuoter from "../../../Quoters/LeaseQuoter/LeaseQuoter";
import {BureauFavorite} from '../../../../Data/BureauFavorites';
import {getAppQuoteByType} from "../../../Quoters/IndirectQuoter/IndirectQuoterUtils";
import {IndirectQuoteType} from "../../../../Data/IndirectQuoteType";
import {DownPaymentHistoryCard} from "../DownPaymentHistoryCard";
import {useDownPaymentCardCharges} from "../../../../Data/DownPaymentCardData";
import {AmsDeskingHistoryType} from "../../../../Interfaces/Application/IAmsDeskingHistory";

export interface OverviewProps {
  configuration: IConfiguration;
  navigate: (tabName: TabName) => void;
  setNestedTabNameFlag: (arg0: string | undefined) => void;
  handleSaveAppFromOverview: (currentApp: IApplication, originalApplicationStatus: string, bypassAppRequirements: boolean) => Promise<void>;
}

export const Overview: React.FC<OverviewProps> = props => {
  const currentApp = useSelector((state: AppState) => state.applications.editingApp);
  const appLoadedBusinessRulesHaveRun = useSelector((state: AppState) => state.applications.appLoadedBusinessRulesHaveRun);
  const previousAmsLoans = useSelector((state: AppState) => state.applications.previousAmsLoans);
  const applicationDownPaymentResponses = useSelector((state: AppState) => state.applications.applicationDownPaymentResponses);
  const { data: cardCharges } = useDownPaymentCardCharges(currentApp?.id);
  const { data: outstandingAppCustomerStips } = useOutstandingApplicationCustomerStipulations(currentApp ? currentApp.id : Constants.emptyGuid, false);
  const primary = currentApp && getApplicant(currentApp, Constants.roleApplicantId, true);
  const cobuyer1 = currentApp && getApplicant(currentApp, Constants.roleCoApplicant1Id, true);
  const dispatch = useDispatch();
  const activeUser = useSelector((state: AppState) => state.applications.loggedInUser?.employee);
  const [vehicleToEdit, setVehicleToEdit] = useState<Vehicle | null>(null);
  const organizations = useSelector((state: AppState) => state.applications.lookups.organizations);
  const incompleteAppCustomerStipulationCategories = useSelector((state: AppState) => state.applications.incompleteAppCustomerStipulationCategories);
  // const dealertrackDealerDecisionData = useSelector((state: AppState) => state.applications.dealertrackDealerDecisionData);
  const dealerRequestedQuote = getAppQuoteByType(currentApp, IndirectQuoteType.DealerRequested);
  const labelProps: GridProps = { xs: 6, sm: 5, };
  const valueProps: GridProps = { xs: 6, sm: 7, };
  const googleMapsKey = props.configuration?.googleMapsEmbedKey;
  const origin = organizations.find(o => o.id === currentApp?.organizationId)?.mapAddress;
  const primaryCustomerAddress = primary?.customer?.customerAddress?.[0];
  const lookups = useSelector((state: AppState) => state.applications.lookups);
  const quotePayTypes = useSelector((state: AppState) => state.applications.quotePayTypes);
  const applicationPerformanceIndicatorEvaluations = useSelector((state: AppState) => state.applications.applicationPerformanceIndicatorEvaluations);
  const favorites: BureauFavorite[] = useSelector((state: AppState) => state.applications.lookups.bureauFavorites.favorites);
  const businessRulesEngine = useBusinessRulesEngine();
  const employees: IEmployee[] = useSelector((state: AppState) => state.applications.employees);

  const [showMoreQuoteFields, setShowMoreQuoteFields] = useState(!props.configuration?.isFinanceCompany);
  const [applicantScore, setApplicantScore] = useState(0);
  const [calculatingScore, setCalculatingScore] = useState(true);
  useEffect(() => {
    setShowMoreQuoteFields(!props.configuration?.isFinanceCompany);
  }, [props.configuration?.isFinanceCompany]);

  useEffect(() => {
    if (currentApp && outstandingAppCustomerStips) {
      const primaryApplicant = currentApp.appCustomers.find(ac => ac.customerRoleId?.toLowerCase() === Constants.roleApplicantId?.toLowerCase());
      const incompleteCustomerStipulationCategoriesForPrimaryApplicant = outstandingAppCustomerStips
        .filter(os => os.applicationCustomerId && primaryApplicant && os.applicationCustomerId.toLowerCase() === primaryApplicant?.id?.toLowerCase() && os.numberOfDocumentsAccepted < os.numberOfDocumentsRequired)
        .map(osacs => osacs.stipulationCategoryId);
      dispatch({ type: SET_INCOMPLETE_APPCUSTOMERS_STIPULATION_CATEGORIES, payload: incompleteCustomerStipulationCategoriesForPrimaryApplicant });
    }
  }, [currentApp, outstandingAppCustomerStips]);

  useEffect(() => {
    if (incompleteAppCustomerStipulationCategories) {
      businessRulesEngine({ action: "Modify" });
    }
  }, [incompleteAppCustomerStipulationCategories]);

  useEffect(() => {
    if (currentApp && props.configuration?.featureFlags[FeatureFlagIds.UseInternalAmsScore] === true) {
      setCalculatingScore(true);
      dispatch(getApplicationPerformanceEvaluation({ applicationId: currentApp.id, allClients: true }))
    }
  }, [currentApp, props.configuration]);

  useEffect(() => {
    if (applicationPerformanceIndicatorEvaluations?.length > 0 === true) {
      localforage.getItem('scoringWeights').then((selectedWeights) => {
        if (selectedWeights) {
          let score = 0;
          applicationPerformanceIndicatorEvaluations.filter(api => api.useForApplicationScoring).forEach(api => {
            const weights: { itemName: string, itemWeight: number }[] = selectedWeights as { itemName: string, itemWeight: number }[];
            const weight = weights.find((item: any) => item.itemName === api.name);
            if (weight) {
              score += (api.percentOfLoansInGoodStanding * weight.itemWeight) / 10;
            }
            setApplicantScore(score);
          });
        }
      });
    }
    setCalculatingScore(false);
  }, [applicationPerformanceIndicatorEvaluations]);

  const address = !primaryCustomerAddress
    ? null
    : encodeURIComponent(`${primaryCustomerAddress?.street1 || ''} ${primaryCustomerAddress?.street2 || ''} ${primaryCustomerAddress?.city || ''} ${primaryCustomerAddress.state?.abbreviation || ''} ${primaryCustomerAddress?.zipCode || ''} ${primaryCustomerAddress?.country || ''}`?.trim() ?? '');

  const googleMapsIframeSrc =
    !props.configuration?.googleMapsEmbedKey
      ? ''
      : origin
        ? `https://www.google.com/maps/embed/v1/directions?key=${googleMapsKey}&origin=${origin}&destination=${address}`
        : `https://www.google.com/maps/embed/v1/place?key=${googleMapsKey}&q=${address}`;

  // NON auto-calculated quote for this application
  const underwriterAppQuote = getAppQuoteByType(currentApp, IndirectQuoteType.Underwriter)

  // Update AMS vehicle record and our DB Vehicle entity.
  const updateAmsVehicle = async (vehicle: Vehicle, applicationId: string, isIndirectApplication: boolean) => {
    try {
      await updateAmsVehicleAsync(dispatch, vehicle, applicationId, isIndirectApplication);
    } finally {
      setVehicleToEdit(null);
    }
  }

  const preventDeskingDueToIncompleteChecklist = currentApp &&
    props.configuration?.requiredChecklistsForDesking &&
    props.configuration.requiredChecklistsForDesking.length > 0
    ? isAnyRequiredChecklistIncomplete(props.configuration.requiredChecklistsForDesking, currentApp?.applicationChecklists)
    : false;

  const preventDeskingDueToIncompleteStips = props.configuration.featureFlags?.[FeatureFlagIds.DisableDeskingIfRequiredStipsIncomplete] === true
    && (isNil(outstandingAppCustomerStips) || outstandingAppCustomerStips.some(oacs => oacs.numberOfDocumentsSubmitted < oacs.numberOfDocumentsRequired));



  const preventDesking = preventDeskingDueToIncompleteChecklist || preventDeskingDueToIncompleteStips;

  // If financeQuoterCutoverDate is defined and the application came in before the cutover date, it will show the old quoter for Easton instead of the Carleton-enabled version
  const applicationIsBeforeOldQuoterCutoff = isApplicationBeforeOldQuoterCutoff(currentApp?.applicationDate, props.configuration.financeQuoterCutoverDate);

  const updateApplication = (fn: (app: IApplication) => IApplication | void) => {
    const newValue = produce(currentApp, fn);
    if (newValue) {
      dispatch(updateEditingApp(newValue, businessRulesEngine));
    }
  };

  if (props.configuration.clientId === 'ATC' && currentApp) {
    return <OverviewATC
      {...props}
      appCustomerStipDocRequirements={outstandingAppCustomerStips || []}
      appCustomers={currentApp.appCustomers || []}
      navigate={props.navigate}
      setNestedTabNameFlag={props.setNestedTabNameFlag}
      handleSaveAppFromOverview={props.handleSaveAppFromOverview}
    />;
  }

  // const showQuoter = !!currentApp && currentApp.loanOriginationType === ApplicationLoanOriginationType.Direct
  //   && props.configuration.featureFlags?.[FeatureFlagIds.CanViewQuoterScreen] === true
  //   && !applicationIsBeforeOldQuoterCutoff;

  const isIndirectConvertedToFinanceResult = isIndirectConvertedToFinance(currentApp, props.configuration);

  const showFinanceQuoter = usesFinanceQuote(currentApp?.quoteType) || isIndirectConvertedToFinanceResult;
  const showQuoter = showFinanceQuoter || currentApp?.quoteType === ApplicationQuoteType.Lease;

  const updateApplicationQuoteType = (newQuoteType: ApplicationQuoteType): void => {
    if (!currentApp || newQuoteType === currentApp.quoteType) {
      return; // Nothing changed
    }

    const updatedApp = produce(currentApp, newCurrentApp => {
      newCurrentApp.quoteType = newQuoteType;
      // A slot the new quote type no longer shows is emptied, so saving ends that assignment.
      newCurrentApp.applicationAssignments = clearSlotsHiddenByQuoteTypeChange(
        lookups.applicationAssignableRoles ?? [], currentApp.applicationAssignments ?? [], currentApp.quoteType, newQuoteType);
    });

    dispatch(updateEditingApp(updatedApp, businessRulesEngine));
  }

  const getDecisionBoxForClientAndRole = (): JSX.Element => {
    if (!currentApp) {
      return <div></div>
    }

    // Direct apps
    if (showQuoter) {
      if (showFinanceQuoter) {
        return <FinanceQuoterContainer
          {...props}
          financeLoanClasses={lookups.financeLoanClasses}
          lookups={lookups}
          preventDesking={preventDesking}
        />
      }
      if (currentApp.quoteType === ApplicationQuoteType.Lease && lookups.leaseLoanClasses.length > 0 && quotePayTypes.length > 0 && currentApp.quoteType === ApplicationQuoteType.Lease) {
        return <LeaseQuoter
          {...props}
          lookups={lookups}
          preventDesking={preventDesking}
        />
      }
    }

    return (
      <div>
        {canUnderwrite(activeUser)
          ?
          // This displays the 3 quotes one of which is indirect
          <DecisionBoxIndirectUnderwriterView
            updateApplication={updateApplication}
            handleSaveAppFromOverview={props.handleSaveAppFromOverview}
            configuration={props.configuration}
          />
          :
          // This displays either 1 or 2 quotes. EAM no longer uses this but for old applications this
          // will be used. It is not currently used for any other client, but the thought was that
          // Indirect Dealers could be given logins to the application and they would see this view.
          <DecisionBoxIndirectDealerView
            updateApplication={updateApplication}
            configuration={props.configuration} />
        }
        <StatusWorkflowComponent
          style={{marginTop: '10px'}}
          disableDeskingOrPosting={preventDesking}
        />
      </div>
    );
  }

  const appStatusesPastSubmission = [
    Constants.applicationStatuses.desking,
    Constants.applicationStatuses.underwriting,
    Constants.applicationStatuses.approved,
    Constants.applicationStatuses.denied,
    Constants.applicationStatuses.conditionallyApproved,
  ];

  const showTheIndirectApplicationDecisionInfoCard: boolean =
    !!currentApp
    && (currentApp.quoteType === ApplicationQuoteType.Indirect)
    && appStatusesPastSubmission.includes(currentApp.appStatusId.toUpperCase());
  
  // Getting the posted date if it was posted successfully
  const postedDate = currentApp?.latestAmsDeskingHistory && currentApp.latestAmsDeskingHistory.historyType === AmsDeskingHistoryType.Post
    && hasValue(currentApp.latestAmsDeskingHistory.amsAccountNumber)
    ? currentApp.latestAmsDeskingHistory.createdDate
    : null;

  return (
    <div className={styles.overviewContainer}>
      {hasExistingCustomerInformation(currentApp) && (
        <ExistingCustomerInformation appCustomers={currentApp!.appCustomers} />
      )}
      {previousAmsLoans && previousAmsLoans.length > 0 &&
        <PreviousAmsLoansCard
          previousAmsLoans={previousAmsLoans}
          canViewLoanDetails={activeUser ? userHasPermission(activeUser, permissions.viewPreviousAmsLoanDetails, PermissionSet.R) : false}
        />
      }
      <Grid container spacing={1} style={{ paddingBottom: '100px' }}>

        {/*LEFT COLUMN*/}
        <Grid item xs={12} lg={3} container spacing={1} direction="column">
          <Grid item className={styles.columnSize}>
            <ApplicationDetailsCard
              {...props}
              updateApplication={updateApplication}
              handleSaveAppFromOverview={props.handleSaveAppFromOverview}
            />
          </Grid>
          {currentApp &&
            <Grid item className={styles.columnSize}>
              <ApplicationSummaryCard
                {...props}
                currentApp={currentApp}
                navigate={props.navigate}
                contactDetailUrl={currentApp?.contactDetailUrl}
              />
            </Grid>
          }
          <Grid item className={styles.columnSize}>
            <VehicleInfoCard
              {...props}
              navigate={props.navigate}
              showVehicleComponent={!showQuoter} // right now, the vehicle info is shown in the Quoter, so not needed separately
            />
          </Grid>
          {currentApp && applicationIsPastDesking(currentApp.appStatusId) && currentApp.latestAmsDeskingHistory &&
            <Grid item>
              <AmsApplicationInfoCard
                {...props}
                latestAmsDeskingHistory={currentApp.latestAmsDeskingHistory}
                employees={employees}
              />
            </Grid>
          }
          {currentApp && favorites?.length > 0 &&
            <Grid item>
              <CustomerCreditBreakdownCard
                {...props}
                currentApp={currentApp}
                favorites={favorites}
                configuration={props.configuration}
              />
            </Grid>
          }
          {currentApp && props.configuration.featureFlags[FeatureFlagIds.ShowMissingStipulations] && (
            <Grid item>
              <InvalidStipulationsCard
                appCustomerStipDocRequirements={outstandingAppCustomerStips || []}
                appCustomers={currentApp.appCustomers || []}
                navigate={props.navigate}
              />
            </Grid>
          )}
          {currentApp && showTheIndirectApplicationDecisionInfoCard &&
            <Grid item>
              <IndirectApplicationDecisionInfoCard
                {...props}
                underwriterQuote={underwriterAppQuote?.quote}
                currentApp={currentApp}
                labelProps={labelProps}
                valueProps={valueProps}
                primaryApplicant={primary}
              />
            </Grid>
          }
        </Grid>

        {/*MIDDLE COLUMN*/}
        <Grid item xs={12} lg={6}>
          {/* Also while the application has card down payments but no deposits yet, so they can be finished (A13). */}
          {((applicationDownPaymentResponses?.length ?? 0) > 0 || (cardCharges?.length ?? 0) > 0) &&
            <DownPaymentHistoryCard
              applicationDownPaymentResponses={applicationDownPaymentResponses ?? []}
              postedDate={postedDate}
              configuration={props.configuration}
            />
          }
          {getDecisionBoxForClientAndRole()}
        </Grid>


        {/*RIGHT COLUMN*/}
        <Grid item xs={12} lg={3} container spacing={2} direction="column">

          {currentApp && appLoadedBusinessRulesHaveRun && props.configuration.featureFlags[FeatureFlagIds.HideCreditQualityAndRiskMetricsCardLOS] !== true &&
            <Grid item>
              {props.configuration.featureFlags[FeatureFlagIds.UseAMSScoring] !== true && (
                <CreditQualityAndRiskMetricsCard
                  {...props}
                  currentApp={currentApp!}
                  underwriterQuote={underwriterAppQuote?.quote}
                  dispatch={dispatch}
                  cobuyer1={cobuyer1}
                />
              )}
              {props.configuration.featureFlags[FeatureFlagIds.UseAMSScoring] === true && (
                <div className={styles.applicantPerformanceIndicatorRankViewer} style={{ paddingRight: '8px' }}>
                  <ApplicantPerformanceIndicatorRankViewer
                    onRefreshScore={() => {
                      setCalculatingScore(true);
                      dispatch(getApplicationPerformanceEvaluation({ applicationId: currentApp.id, allClients: true }));
                    }}
                    applicantScore={applicantScore ? Math.round(applicantScore).toString() : ''}
                    performanceIndicators={applicationPerformanceIndicatorEvaluations.map(api => {
                      return {
                        ...api
                      } as ApplicationPerformanceIndicatorEvaluationResult;
                    })}
                  />
                  {calculatingScore && (
                    <LoadingPanel style={{ position: 'absolute', top: '0px', left: '0px', width: '100%', height: '100%' }} label={`Calculating`} />
                  )}
                </div>
              )}

            </Grid>
          }

          {currentApp && props.configuration.featureFlags[FeatureFlagIds.DisplayNotesOnOverview] && (
            <Grid item>
              <NotesCard
                navigate={props.navigate}
              />
            </Grid>
          )}

          {currentApp && dealerRequestedQuote && (
            <Grid item>
              <IndirectApplicationHistoryCard
                currentApp={currentApp}
                dispatch={dispatch}
              />
            </Grid>
          )}

          {currentApp && props.configuration.featureFlags[FeatureFlagIds.UseLOSChat] && (
            <Grid item>
              <Card>
                <CardHeader
                  avatar={<ChatIcon fontSize="small" />}
                  title="Chat"
                />
                <ApplicationChatComponent
                  navigate={props.navigate}
                  currentApp={currentApp}
                  organizations={organizations}
                  configuration={props.configuration}
                />
              </Card>
            </Grid>
          )}
          { props.configuration.featureFlags[FeatureFlagIds.HideMapLOS] !== true &&
          <Grid item>
            <Card>
              <CardHeader
                avatar={<MapIcon fontSize="small" />}
                title="Map"
                action={
                  <IconButton size="small" onClick={() => props.navigate("Applicant")}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                }
              />
              {googleMapsIframeSrc &&
                <iframe
                  src={googleMapsIframeSrc}
                  width="100%"
                  height="450"
                  frameBorder="0"
                  style={{ border: 0 }}
                  allowFullScreen
                  aria-hidden="false"
                  tabIndex={0}>
                </iframe>
              }
            </Card>
          </Grid>
          }
          {/* Hack to add spacing at the bottom to account for text messages  */}
          <Grid item>
            <div style={{ height: '100px' }}></div>
          </Grid>
        </Grid>
      </Grid>
      {vehicleToEdit && (
        <VehicleEditorDialog
          vehicle={vehicleToEdit}
          canEditFullVehicle={props.configuration?.featureFlags[FeatureFlagIds.AllowVehicleFullEdit] === true}
          canEditInventory={activeUser && userHasPermission(activeUser, permissions.inventoryMileage, PermissionSet.U)}
          onClose={() => setVehicleToEdit(null)}
          onSaveVehicle={async (vehicle: Vehicle) => {
            await updateAmsVehicle(vehicle, currentApp!.id, (currentApp?.quoteType === ApplicationQuoteType.Indirect));
          }}
        />
      )}
    </div>
  );
}
